import { useState, useEffect } from 'react';
import { Typography, Box, Grid, Card, CardContent, Paper, Alert, CircularProgress, Snackbar, useTheme, useMediaQuery } from '@mui/material';
import { Person, Business, CalendarMonth, EventAvailable, TrendingUp } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dashboardService from '../services/dashboardService';
import { useUpdateOnboardingStats } from '../hooks/useOnboarding';
import { useContextualHint } from '../hooks/useContextualHint';
import ContextualHint from '../components/ContextualHint';

export default function DashboardPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  const [stats, setStats] = useState({ 
    professionals: 0, 
    services: 0, 
    places: 0, 
    schedules: 0,
    appointments: 0,
    pendingAppointments: 0,
    appointmentsToday: 0,
    appointmentsThisWeek: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Só atualiza onboarding quando os dados reais forem carregados
  useUpdateOnboardingStats(stats, loading);

  // Obter dica contextual baseada nos stats
  const hint = useContextualHint('dashboard', { stats });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await dashboardService.getStats();
        setStats(data);
      } catch (err) {
        console.error(err);
        setError('Erro ao carregar estatísticas');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Dashboard
      </Typography>

      {/* Dica Contextual */}
      <ContextualHint hint={hint} />

      {/* Cards de estatísticas */}
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 2 : 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }}
            onClick={() => navigate('/professionals')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Profissionais
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.professionals}
                  </Typography>
                </Box>
                <Person sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }}
            onClick={() => navigate('/places')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Locais
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.places}
                  </Typography>
                </Box>
                <Business sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }}
            onClick={() => navigate('/schedules')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Agendas
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.schedules}
                  </Typography>
                </Box>
                <CalendarMonth sx={{ fontSize: 48, color: 'info.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }}
            onClick={() => navigate('/appointments')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Agendamentos
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.appointments}
                  </Typography>
                </Box>
                <EventAvailable sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Próximos passos */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          🚀 Primeiros Passos
        </Typography>
        <Box component="ol" sx={{ pl: 2 }}>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            <strong>Cadastre seus perfis profissionais</strong> em "Profissionais" e configure os serviços oferecidos
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            <strong>Adicione seus locais de atendimento</strong> em "Locais" (clínica, consultório, etc.)
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            <strong>Configure suas agendas</strong> em "Agendas", definindo horários e turnos de trabalho
          </Typography>
          <Typography component="li" variant="body1">
            <strong>Gerencie suas ausências</strong> em "Ausências" (férias, feriados, compromissos)
          </Typography>
        </Box>
      </Paper>

      {/* Recursos do sistema */}
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mt: isMobile ? 1 : 2 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TrendingUp color="primary" />
              <Typography variant="h6">Recursos Principais</Typography>
            </Box>
            <Box component="ul" sx={{ pl: 2 }}>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                Múltiplos perfis profissionais
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                Gestão de múltiplos locais
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                Configuração flexível de horários
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                Bloqueio de períodos (férias, etc.)
              </Typography>
              <Typography component="li" variant="body2">
                Geração automática de slots de agendamento
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              💡 Dicas
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                Use nomes descritivos para facilitar identificação
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                Configure intervalos entre consultas se necessário
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                Bloqueie feriados e períodos de férias com antecedência
              </Typography>
              <Typography component="li" variant="body2">
                Mantenha seus dados atualizados para melhor experiência
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
        message={error}
      />
    </Box>
  );
}
